import React from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import renderer from 'react-test-renderer';

import theme from 'styles/themes/wikifactory';

import styled, { css, ThemeProvider } from 'styled-components';
import { generateMedia } from 'styled-media-query';

export const customMedia = generateMedia({
  ...theme.breakpoints,
});

// iterate through the sizes and create a media template
export const media = Object.keys(theme.breakpoints).reduce(
  (accumulator, label) => {
    // use em in breakpoints to work properly cross-browser and support users
    // changing their browsers font-size: https://zellwk.com/blog/media-query-units/
    accumulator[label] = (...args) => css`
      @media (min-width: ${theme.breakpoints[label]}) {
        ${css(...args)};
      }
    `;
    return accumulator;
  },
  {},
);

// Convert hexadecimal colors (short and regular) to RGB or RGBA colors
export const hexa = (hex, alpha) => {
  const isNumber = typeof alpha === 'number';
  if (isNumber && alpha <= 0) return 'transparent';

  const isShort = hex.length === 4;
  const buildShort = character => parseInt(`0x${character}${character}`, 16);
  const r = isShort ? buildShort(hex[1]) : parseInt(hex?.slice(1, 3), 16);
  const g = isShort ? buildShort(hex[2]) : parseInt(hex?.slice(3, 5), 16);
  const b = isShort ? buildShort(hex[3]) : parseInt(hex?.slice(5, 7), 16);

  if (isNumber) return `rgba(${r}, ${g}, ${b}, ${alpha < 1 ? alpha : 1})`;
  return `rgb(${r}, ${g}, ${b})`;
};

// Test render with theme
export function renderWithTheme(component) {
  return renderer.create(
    <ThemeProvider theme={theme}>{component}</ThemeProvider>,
  );
}

// Text truncated
export function truncateWidth(width) {
  return `
    width: ${width};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `;
}

// Pseudo element
export function pseudo(position, width, height) {
  return `
    content: '';
    width: ${width};
    height: ${height};
    position: ${position};
  `;
}

// Base Container CSS
export const cssBaseContainer = css`
  max-width: ${props => props.theme.layout.maxWidth};
  padding: ${props =>
    `${props.theme.spacing.medium} ${props.theme.layout.paddingInline}`};
  margin: 0 auto;
`;

// Dropdown CSS

export const cssDropdown = css`
  min-width: 260px;
  position: absolute;
  z-index: 5;
  background-color: ${props => props.theme.color.wfWhite};
  padding: ${props => props.theme.spacing.xsmall} 0;
  box-shadow: rgba(67, 90, 111, 0.416) 0 0 1px,
    rgba(67, 90, 111, 0.3) 0 5px 8px -4px;
`;

export const cssDropdownItem = css`
  color: ${props => props.theme.color.wfBlack};
  margin: ${props => props.theme.spacing.xsmall} 0;
  cursor: pointer;
  background-color: ${props =>
    props.isActive ? props.theme.color.wfGrey200 : props.theme.color.wfWhite};
  padding: ${props =>
    `${props.theme.spacing.small} ${props.theme.spacing.default}`};
  transition: padding 250ms ease-in-out;
  font-size: ${props => props.theme.font.size.default};
  &:hover {
    background-color: ${props =>
      props.danger ? props.theme.color.wfOrange : props.theme.color.primary};
    color: ${props => props.theme.color.wfWhite};
  }
`;

export const Spacer = styled.div`
  margin-top: ${props =>
    theme.spacing[props.size] ? theme.spacing[props.size] : '0px'};
  margin-right: ${props =>
    theme.spacing[props.size] ? theme.spacing[props.size] : '0px'};
`;

/*
Spacer Documentation

Spacing sizes file:
alpha/frontend/src/styles/themes/wikifactory/styles/global.js

import { Spacer } from 'styles/utils';
<Spacer size='default' />
*/

export const remToPxNumber = rem => {
  const fontSize =
    window?.getComputedStyle(document?.documentElement)?.fontSize || '16px';
  const remBase = Number(fontSize?.replace('px', '')) || 16;
  return Number(rem?.replace('rem', '')?.replace('em', '')) * remBase;
};

export const isMobile = (breakpoint = 'medium') =>
  (window?.innerWidth || document?.documentElement?.clientWidth) <=
  remToPxNumber(theme.breakpoints[breakpoint]);

export const marginReplacer = string => {
  if (typeof string !== 'string') return string;
  let output = string;

  // The keys need to be sort (the ones with the a bigger length first) to avoid confusion between similar key names.
  // For example, if we replace xlarge 1st and the target is xxxlarge it will output 'xx3rem' and it will break the CSS. If we revert the order we remove that issue.
  const keyslist = Object.keys(theme.spacing).sort(
    (a, b) => b.length - a.length,
  );
  keyslist.forEach(key => {
    // The same as replaceAll
    output = output.replace(new RegExp(key, 'g'), () => theme.spacing[key]);
  });

  return output;
};

export const themeColorKeys = Object.keys(theme.color);

export const isThemeColor = string => {
  return themeColorKeys.includes(string);
};

export const defaultTransition = property => {
  if (Array.isArray(property)) {
    return `transition: ${property
      .map(value => {
        if (!value) return null;
        return `${value} ${theme.transition.duration} ${theme.transition.func}`;
      })
      .filter(output => output !== null)
      .join(', ')};`;
  }

  return `transition: ${property || 'all'} ${theme.transition.duration} ${
    theme.transition.func
  };`;
};
